/**
 * Tricolor Wave Effect (Republic Day / Independence Day)
 * Subtle saffron–white–green gradient shimmer in header area. Pride, unity – not loud.
 */

import { useEffect, useRef } from 'react'

interface TricolorWaveProps {
  intensity?: 'low' | 'medium'
}

// Indian flag colors (subtle)
const SAFFRON = 'rgba(255, 153, 51,'
const WHITE = 'rgba(255, 255, 255,'
const GREEN = 'rgba(19, 136, 8,'

export function TricolorWave({ intensity = 'low' }: TricolorWaveProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const phaseRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const bandHeight = intensity === 'low' ? 4 : 6
    const alpha = intensity === 'low' ? 0.06 : 0.09

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      phaseRef.current += 0.008

      const wave = Math.sin(phaseRef.current) * 0.5 + 0.5

      // Thin horizontal bands at top (header area)
      const top = 0
      ctx.fillStyle = `${SAFFRON} ${alpha * (0.7 + wave * 0.3)})`
      ctx.fillRect(0, top, canvas.width, bandHeight)

      ctx.fillStyle = `${WHITE} ${alpha * (0.6 + wave * 0.2)})`
      ctx.fillRect(0, top + bandHeight, canvas.width, bandHeight)

      ctx.fillStyle = `${GREEN} ${alpha * (0.7 + wave * 0.3)})`
      ctx.fillRect(0, top + bandHeight * 2, canvas.width, bandHeight)

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  )
}
